// First-run setup, login and MFA (enrollment + verification). Passwords are never stored
// in the browser; the session is an HttpOnly cookie set by the API.
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useI18n } from "../i18n/index.tsx";
import { isApiError } from "../api/client.ts";
import { confirmMfa, enrollMfa, login, setup } from "../api/endpoints.ts";
import { verifyMfa } from "../api/endpoints-wave5.ts";
import type { MfaEnrollment } from "../api/types.ts";
import { useSession } from "../session/SessionProvider.tsx";
import { Button } from "../components/Button.tsx";
import { TextField } from "../components/Field.tsx";
import { Banner, ErrorBanner, LoadingBlock } from "../components/Feedback.tsx";
import { LogoIcon, RefreshIcon, ShieldIcon } from "../components/icons.tsx";
import { MfaSecret } from "../features/security/MfaSecret.tsx";

function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <main className="auth" id="main">
      <div className="auth-card card">
        <div className="brand brand--center">
          <LogoIcon size={36} className="brand-logo" />
          <div className="brand-text">
            <span className="brand-name">Primage</span>
            <span className="brand-sub">Studio</span>
          </div>
        </div>
        <div className="stack" style={{ gap: 4, textAlign: "center" }}>
          <h1 className="auth-title">{title}</h1>
          {subtitle ? <p className="muted">{subtitle}</p> : null}
        </div>
        {children}
      </div>
    </main>
  );
}

function errorText(e: unknown, fallback: string): string {
  if (isApiError(e)) return e.message;
  return fallback;
}

export function SetupPage() {
  const { t, locale } = useI18n();
  const { refresh, setStatus, setPendingEnrollment } = useSession();
  const [form, setForm] = useState({ organization_name: "", display_name: "", email: "", password: "", confirm: "", setup_token: "" });
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const update = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.organization_name.trim()) next["organization_name"] = t("validation.required");
    if (!form.display_name.trim()) next["display_name"] = t("validation.required");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next["email"] = t("validation.email");
    if (form.password.length < 12) next["password"] = t("validation.passwordLength", { n: 12 });
    if (form.confirm !== form.password) next["confirm"] = t("validation.passwordMatch");
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const email = form.email.trim();
      const result = await setup({ organization_name: form.organization_name.trim(), display_name: form.display_name.trim(), email, password: form.password, locale }, form.setup_token.trim() || undefined);
      setPendingEnrollment(result.mfa_enrollment);
      // Setup does not open a session: sign in once with the same credentials (memory only).
      await login({ email, password: form.password });
      setForm((f) => ({ ...f, password: "", confirm: "" }));
      await refresh();
    } catch (err) {
      if (isApiError(err) && err.code === "version_conflict") setAlreadyDone(true);
      setSubmitError(errorText(err, t("error.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("setup.title")} subtitle={t("setup.subtitle")}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        {submitError ? (
          <Banner
            tone="error"
            role="alert"
            title={alreadyDone ? t("setup.alreadyDone") : t("setup.failed")}
            action={
              alreadyDone ? (
                <Button size="sm" onClick={() => setStatus("signed_out")}>
                  {t("auth.signIn")}
                </Button>
              ) : undefined
            }
          >
            {submitError}
          </Banner>
        ) : null}
        <TextField label={t("setup.organization")} value={form.organization_name} onChange={update("organization_name")} error={errors["organization_name"] ?? null} autoComplete="organization" required />
        <TextField label={t("setup.displayName")} value={form.display_name} onChange={update("display_name")} error={errors["display_name"] ?? null} autoComplete="name" required />
        <TextField label={t("auth.email")} type="email" value={form.email} onChange={update("email")} error={errors["email"] ?? null} autoComplete="email" required />
        <TextField label={t("auth.password")} type="password" value={form.password} onChange={update("password")} error={errors["password"] ?? null} autoComplete="new-password" hint={t("setup.passwordHint")} required />
        <TextField label={t("setup.confirmPassword")} type="password" value={form.confirm} onChange={update("confirm")} error={errors["confirm"] ?? null} autoComplete="new-password" required />
        <TextField label={t("setup.setupToken")} type="password" value={form.setup_token} onChange={update("setup_token")} error={errors["setup_token"] ?? null} autoComplete="off" hint={t("setup.setupTokenHint")} />
        <p className="field-hint">{t("setup.mfaNext")}</p>
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          {t("setup.submit")}
        </Button>
        <Button variant="ghost" onClick={() => setStatus("signed_out")}>
          {t("setup.haveAccount")}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function LoginPage() {
  const { t } = useI18n();
  const { refresh, setStatus } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needCode, setNeedCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A magic link from the daily e-mail that expired or was already used lands here (/login?magic=expired).
  const [magicExpired] = useState(() => new URLSearchParams(window.location.search).get("magic") === "expired");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login({ email: email.trim(), password, ...(needCode && code ? { totp: code.trim() } : {}) });
      setPassword("");
      if (result.mfa.enrollment_required && !result.mfa.enabled) setStatus("mfa_enrollment");
      else await refresh();
    } catch (err) {
      if (isApiError(err) && err.code === "mfa_required") {
        setNeedCode(true);
        if (needCode) setError(err.message);
      } else if (isApiError(err) && err.code === "unauthenticated") {
        setError(t("auth.invalidCredentials"));
      } else {
        setError(errorText(err, t("error.generic")));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.signInTitle")} subtitle={t("auth.signInSubtitle")}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        {magicExpired ? (
          <Banner tone="info" role="status" title={t("auth.magicExpiredTitle")}>
            {t("auth.magicExpiredBody")}
          </Banner>
        ) : null}
        {error ? (
          <Banner tone="error" role="alert">
            {error}
          </Banner>
        ) : null}
        <TextField label={t("auth.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        <TextField label={t("auth.password")} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        {needCode ? (
          <TextField
            label={t("auth.mfaCode")}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint={t("auth.mfaCodeHint")}
            autoFocus
          />
        ) : null}
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          {t("auth.signIn")}
        </Button>
        <Button variant="ghost" onClick={() => setStatus("setup_required")}>
          {t("auth.firstRun")}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function MfaEnrollmentPage() {
  const { t } = useI18n();
  const { refresh, signOut, pendingEnrollment, setPendingEnrollment } = useSession();
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(pendingEnrollment);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (pendingEnrollment && attempt === 0) return;
    let cancelled = false;
    setLoadError(null);
    enrollMfa()
      .then((r) => !cancelled && setEnrollment(r))
      .catch((e: unknown) => !cancelled && setLoadError(e));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError(t("auth.mfaCodeInvalid"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmMfa(code.trim());
      setPendingEnrollment(null);
      await refresh();
    } catch (err) {
      setError(errorText(err, t("error.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("mfa.enrollTitle")} subtitle={t("mfa.enrollSubtitle")}>
      {loadError ? (
        <ErrorBanner error={loadError} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !enrollment ? (
        <LoadingBlock label={t("common.loading")} />
      ) : (
        <form className="stack" onSubmit={onSubmit} noValidate>
          <ol className="mfa-steps">
            <li>{t("mfa.step1")}</li>
            <li>
              {t("mfa.step2")}
              <MfaSecret enrollment={enrollment} />
            </li>
            <li>{t("mfa.step3")}</li>
          </ol>
          {error ? (
            <Banner tone="error" role="alert">
              {error}
            </Banner>
          ) : null}
          <TextField
            label={t("auth.mfaCode")}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <Button type="submit" variant="primary" size="lg" loading={busy} icon={<ShieldIcon size={16} />}>
            {t("mfa.confirm")}
          </Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            {t("user.signOut")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

/**
 * Session exists but MFA was not verified yet (e.g. opened by a magic link from the daily e-mail): the TOTP code
 * confirms it (POST /v1/auth/mfa/verify). Signing in again stays available.
 */
export function MfaVerifyPage() {
  const { t } = useI18n();
  const { refresh, signOut, setStatus } = useSession();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError(t("auth.mfaCodeInvalid"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(code.trim());
      await refresh();
    } catch (err) {
      setCode("");
      // No authenticator enrolled yet: the enrollment flow applies instead.
      if (isApiError(err) && err.code === "version_conflict" && err.details?.["cause"] === "not_enrolled") setStatus("mfa_enrollment");
      else setError(errorText(err, t("error.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("mfa.verifyTitle")} subtitle={t("mfa.verifyCodeSubtitle")}>
      <form className="stack" onSubmit={onSubmit} noValidate data-testid="mfa-verify-form">
        {error ? (
          <Banner tone="error" role="alert">
            {error}
          </Banner>
        ) : null}
        <TextField
          label={t("auth.mfaCode")}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          hint={t("auth.mfaCodeHint")}
          autoFocus
        />
        <Button type="submit" variant="primary" size="lg" loading={busy} icon={<ShieldIcon size={16} />}>
          {t("mfa.verify")}
        </Button>
        <Button variant="ghost" onClick={() => void signOut()}>
          {t("mfa.signInAgain")}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ApiUnreachablePage() {
  const { t } = useI18n();
  const { refresh, error } = useSession();
  return (
    <AuthLayout title={t("auth.unreachableTitle")} subtitle={t("auth.unreachableSubtitle")}>
      <ErrorBanner error={error} title={t("auth.unreachableTitle")} />
      <Button variant="primary" icon={<RefreshIcon size={16} />} onClick={() => void refresh()}>
        {t("common.retry")}
      </Button>
    </AuthLayout>
  );
}
